// Which failed reads of the shared changelog document are expected, for every reader
// of it: the home dashboard's "What's new" card and the boot-time changelog check in
// app.component. One list, so the two readers cannot drift apart again (df-9977117a7491).

import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isTransientFetchError } from 'services/error-patterns';
import { isDatabaseShuttingDown, isDatabaseTornDown, isDatabaseUnavailable } from 'services/database-unavailable-error';

/**
 * Says whether a failed changelog read is an expected kind the card stays quiet about.
 *
 * Expected, because the card can do nothing about them and something else brings the
 * card back:
 *  - the changelog document is not in the local database yet: it re-runs the card when
 *    it syncs in, because the card's watcher is registered on it before the read;
 *  - a transient fetch error (offline, "network connection was lost");
 *  - the database wrapper could not run the read (`DatabaseUnavailableError`, #6245)
 *    for one of two reasons. A transient one (`recovering`) is re-run by the recovery
 *    itself, which notifies every watcher on the database when it finishes. A shutdown
 *    already under way (`isDatabaseShuttingDown`) is our own sign-out or reload. This
 *    is wider than the profile read's rule, which reports a transient reason.
 *
 * Deliberately NOT on the list:
 *  - a missing database handle (`NoDatabaseHandle`). It is not a shutdown: a failed
 *    local-store swap or a purge can cause it, nothing re-runs the card after it, and
 *    this read waits for the database to be ready first, so it is a real fault here.
 *    `isDatabaseShuttingDown` explains why it must not be quietened;
 *  - a CouchDB 401 (owner decision on #6245). The fetch wrapper retries it once with a
 *    fresh login and reports nothing when that also fails, so outside start-up this
 *    reader may be the only record of a refused session.
 */
export function isExpectedChangelogReadFailure(err: unknown): boolean {
  if (isPouchNotFound(err) || isTransientFetchError(err)) return true;
  // not torn down means transient; a shutdown is the one torn-down kind that is ours
  return isDatabaseUnavailable(err) && (!isDatabaseTornDown(err) || isDatabaseShuttingDown(err));
}
